/**
 * Page telemetry (ADR-0016, FR-OBS-02): what happened on this phone, sent to the Campfire host in
 * small batches every 10s, piggy-backed on each move, and when the page is hidden — not only on
 * crashes. Memory only (no browser storage); never sends cards, hands or chat text.
 */
declare const __BUILD__: string;
const BUILD = typeof __BUILD__ !== 'undefined' ? __BUILD__ : 'dev';
const RELAY_SINK = 'https://campfire.apps.c3outpost.com/api/telemetry';

type Event = Record<string, unknown> & { readonly t: number; readonly type: string };
const session = Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => b.toString(16).padStart(2, '0')).join('');
const buffer: Event[] = [];
let inflight = false;
let lastFlush = 0;
let context: Record<string, unknown> = {};
let pending: ReturnType<typeof setTimeout> | null = null;

/** Pages served by a Mac host report to it; phone-hosted and offline pages report to the relay. */
function sink(): string {
  const g = globalThis as { CAMPFIRE_TELEMETRY?: string; CAMPFIRE_WS?: string };
  if (g.CAMPFIRE_TELEMETRY) return g.CAMPFIRE_TELEMETRY;
  if (location.protocol.startsWith('http') && !g.CAMPFIRE_WS) return `${location.origin}/api/telemetry`;
  return RELAY_SINK;
}

/** Stable facts added to every event (room code, seat, game). */
export function setContext(c: Record<string, unknown>): void {
  context = { ...context, ...c };
}

export function track(type: string, data: Record<string, unknown> = {}): void {
  buffer.push({ ...context, ...data, t: Date.now(), type });
  if (buffer.length > 500) buffer.splice(0, buffer.length - 500);
}

function payload(events: Event[]): string {
  return JSON.stringify({ app: 'web', session, build: BUILD, ua: navigator.userAgent, events });
}

export function flush(force = false): void {
  if (!buffer.length) return;
  const wait = 1000 - (Date.now() - lastFlush);
  if (inflight || (!force && wait > 0)) {
    // Too soon (or a send in flight): send as soon as allowed, so a move's logs never wait 10s.
    pending ??= setTimeout(() => {
      pending = null;
      flush(true);
    }, Math.max(wait, 250));
    return;
  }
  lastFlush = Date.now();
  const batch = buffer.splice(0);
  inflight = true;
  // text/plain keeps this a "simple" cross-origin request (no CORS preflight).
  fetch(sink(), { method: 'POST', body: payload(batch), headers: { 'content-type': 'text/plain' }, keepalive: true })
    .catch(() => buffer.unshift(...batch.slice(-200)))
    .finally(() => (inflight = false));
}

export function startTelemetry(): void {
  track('page-load', { path: location.pathname, room: new URLSearchParams(location.search).get('room'), standalone: location.protocol === 'file:' });
  addEventListener('error', (e) => {
    track('js-error', { message: String(e.message).slice(0, 500), at: `${e.filename}:${e.lineno}:${e.colno}`, stack: String((e.error as Error | undefined)?.stack ?? '').slice(0, 2000) });
    flush(true);
  });
  addEventListener('unhandledrejection', (e) => {
    track('js-rejection', { message: String((e.reason as Error | undefined)?.message ?? e.reason).slice(0, 500), stack: String((e.reason as Error | undefined)?.stack ?? '').slice(0, 2000) });
    flush(true);
  });
  document.addEventListener('visibilitychange', () => {
    track('visibility', { state: document.visibilityState });
    if (document.visibilityState === 'hidden' && buffer.length) {
      navigator.sendBeacon?.(sink(), new Blob([payload(buffer.splice(0))], { type: 'text/plain' }));
    }
  });
  setInterval(() => flush(true), 10_000);
}
